import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../core/services/api.service';
import {
  DashboardActiveProjectDetails,
  DashboardActiveProjectListItem,
  DashboardEquipmentProgressResult,
  DashboardManHoursResult,
  DashboardProjectFormByShortCode,
  DashboardProjectManHoursAnalyticsResult,
  DashboardRecurringFailureModesResult,
  DashboardCustomerProgressSummary,
  DashboardProjectCommissioningProgress,
  DashboardProjectSkidStatusSummary
} from '../models/dashboard.model';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  constructor(private apiService: ApiService) {}

  getMyActiveProjects(businessUnitId?: number): Observable<DashboardActiveProjectListItem[]> {
    const suffix = businessUnitId !== undefined && businessUnitId !== null
      ? `?businessUnitId=${encodeURIComponent(String(businessUnitId))}`
      : '';
    return this.apiService.get(`Dashboard/GetMyActiveProjects${suffix}`);
  }

  getMyActiveProjectDetails(projectId: number): Observable<DashboardActiveProjectDetails> {
    return this.apiService.get(`Dashboard/GetMyActiveProjectDetails/${projectId}`);
  }

  getProjectSkidStatusSummaryByUser(userId: number, projectId?: number, businessUnitId?: number): Observable<DashboardProjectSkidStatusSummary[]> {
    const query: string[] = [];
    if (projectId !== undefined && projectId !== null) {
      query.push(`projectId=${encodeURIComponent(String(projectId))}`);
    }
    if (businessUnitId !== undefined && businessUnitId !== null) {
      query.push(`businessUnitId=${encodeURIComponent(String(businessUnitId))}`);
    }
    const suffix = query.length ? `?${query.join('&')}` : '';
    return this.apiService.get(`Dashboard/GetProjectSkidStatusSummaryByUser/${userId}${suffix}`);
  }

  getMyCommissioningProgress(projectId?: number, businessUnitId?: number): Observable<DashboardProjectCommissioningProgress[]> {
    const query: string[] = [];
    if (projectId !== undefined && projectId !== null) {
      query.push(`projectId=${encodeURIComponent(String(projectId))}`);
    }
    if (businessUnitId !== undefined && businessUnitId !== null) {
      query.push(`businessUnitId=${encodeURIComponent(String(businessUnitId))}`);
    }
    const suffix = query.length ? `?${query.join('&')}` : '';
    return this.apiService.get(`Dashboard/GetMyCommissioningProgress${suffix}`);
  }

  getCustomerSkidStatusSummary(projectId: number): Observable<DashboardCustomerProgressSummary | null> {
    return this.apiService.get(`Dashboard/GetCustomerSkidStatusSummary/${projectId}`);
  }

  getCustomerSkidStatusSummaryList(businessUnitId?: number): Observable<DashboardCustomerProgressSummary[]> {
    const suffix = businessUnitId !== undefined && businessUnitId !== null
      ? `?businessUnitId=${encodeURIComponent(String(businessUnitId))}`
      : '';
    return this.apiService.get(`Dashboard/GetCustomerSkidStatusSummaryList${suffix}`);
  }

  getMyEquipmentProgress(businessUnitId?: number): Observable<DashboardEquipmentProgressResult> {
    const suffix = businessUnitId !== undefined && businessUnitId !== null
      ? `?businessUnitId=${encodeURIComponent(String(businessUnitId))}`
      : '';
    return this.apiService.get(`Dashboard/GetMyEquipmentProgress${suffix}`);
  }

  downloadApplicationEquipmentProgress(
    businessUnitId?: number,
    templateGroups?: string[]
  ): Observable<Blob> {
    const query: string[] = [];
    if (businessUnitId !== undefined && businessUnitId !== null) {
      query.push(`businessUnitId=${encodeURIComponent(String(businessUnitId))}`);
    }
    if (templateGroups && templateGroups.length > 0) {
      query.push(`templateGroups=${encodeURIComponent(templateGroups.join(','))}`);
    }

    const suffix = query.length ? `?${query.join('&')}` : '';
    return this.apiService.getBlob(`DashboardExport/ApplicationEquipmentProgress${suffix}`);
  }

  getMyRecurringFailureModes(businessUnitId?: number): Observable<DashboardRecurringFailureModesResult> {
    const suffix = businessUnitId !== undefined && businessUnitId !== null
      ? `?businessUnitId=${encodeURIComponent(String(businessUnitId))}`
      : '';
    return this.apiService.get(`Dashboard/GetMyRecurringFailureModes${suffix}`);
  }

  downloadApplicationRecurringFailureModes(businessUnitId?: number): Observable<Blob> {
    const suffix = businessUnitId !== undefined && businessUnitId !== null
      ? `?businessUnitId=${encodeURIComponent(String(businessUnitId))}`
      : '';
    return this.apiService.getBlob(`DashboardExport/ApplicationRecurringFailureModes${suffix}`);
  }

  getMyManHours(startDate?: string, endDate?: string, businessUnitId?: number): Observable<DashboardManHoursResult> {
    const query: string[] = [];
    if (startDate) {
      query.push(`startDate=${encodeURIComponent(startDate)}`);
    }
    if (endDate) {
      query.push(`endDate=${encodeURIComponent(endDate)}`);
    }
    if (businessUnitId !== undefined && businessUnitId !== null) {
      query.push(`businessUnitId=${encodeURIComponent(String(businessUnitId))}`);
    }

    const suffix = query.length ? `?${query.join('&')}` : '';
    return this.apiService.get(`Dashboard/GetMyManHours${suffix}`);
  }

  downloadApplicationManHours(
    startDate?: string,
    endDate?: string,
    businessUnitId?: number,
    categories?: string[]
  ): Observable<Blob> {
    const query: string[] = [];
    if (startDate) {
      query.push(`startDate=${encodeURIComponent(startDate)}`);
    }
    if (endDate) {
      query.push(`endDate=${encodeURIComponent(endDate)}`);
    }
    if (businessUnitId !== undefined && businessUnitId !== null) {
      query.push(`businessUnitId=${encodeURIComponent(String(businessUnitId))}`);
    }
    if (categories && categories.length > 0) {
      query.push(`categories=${encodeURIComponent(categories.join(','))}`);
    }

    const suffix = query.length ? `?${query.join('&')}` : '';
    return this.apiService.getBlob(`DashboardExport/ApplicationManHours${suffix}`);
  }

  getProjectManHoursAnalytics(
    projectId: number,
    startDate?: string,
    endDate?: string,
    userIds?: number[]
  ): Observable<DashboardProjectManHoursAnalyticsResult> {
    const query: string[] = [];
    if (startDate) {
      query.push(`startDate=${encodeURIComponent(startDate)}`);
    }
    if (endDate) {
      query.push(`endDate=${encodeURIComponent(endDate)}`);
    }
    if (userIds && userIds.length > 0) {
      query.push(`userIds=${encodeURIComponent(userIds.join(','))}`);
    }

    const suffix = query.length ? `?${query.join('&')}` : '';
    return this.apiService.get(`Dashboard/GetProjectManHoursAnalytics/${projectId}${suffix}`);
  }

  downloadProjectCumulativeHours(
    projectId: number,
    startDate?: string,
    endDate?: string,
    userIds?: number[]
  ): Observable<Blob> {
    const query: string[] = [];
    if (startDate) {
      query.push(`startDate=${encodeURIComponent(startDate)}`);
    }
    if (endDate) {
      query.push(`endDate=${encodeURIComponent(endDate)}`);
    }
    if (userIds && userIds.length > 0) {
      query.push(`userIds=${encodeURIComponent(userIds.join(','))}`);
    }

    const suffix = query.length ? `?${query.join('&')}` : '';
    return this.apiService.getBlob(`DashboardExport/ProjectCumulativeHours/${projectId}${suffix}`);
  }

  downloadProjectHoursAllocationBySkid(
    projectId: number,
    startDate?: string,
    endDate?: string,
    userIds?: number[]
  ): Observable<Blob> {
    const query: string[] = [];
    if (startDate) {
      query.push(`startDate=${encodeURIComponent(startDate)}`);
    }
    if (endDate) {
      query.push(`endDate=${encodeURIComponent(endDate)}`);
    }
    if (userIds && userIds.length > 0) {
      query.push(`userIds=${encodeURIComponent(userIds.join(','))}`);
    }

    const suffix = query.length ? `?${query.join('&')}` : '';
    return this.apiService.getBlob(`DashboardExport/ProjectHoursAllocationBySkid/${projectId}${suffix}`);
  }

  getProjectFormsByTemplateShortCode(projectId: number, shortCode: string): Observable<DashboardProjectFormByShortCode[]> {
    const code = encodeURIComponent((shortCode ?? '').trim());
    return this.apiService.get(`Dashboard/GetProjectFormsByTemplateShortCode/${projectId}?shortCode=${code}`);
  }
}
